import React, { useState } from 'react';
import { Modal } from '@/shared/ui/Modal/Modal';
import { useGetClubChangeLogQuery } from '@/entities/Club';
import type { ClubChangeLogItem } from '@/entities/Club';
import { Loader } from '@/shared/ui/Loader/Loader';
import { Button } from '@/shared/ui/Button/Button';
import { formatClubLogValue } from '@/features/ClubChangeLogModal/lib/formatClubLogValue';
import { formatDateTimeForDisplay } from '@/shared/lib/utils/dateFormat';
import { accountDeletionEventLabels } from '@/shared/constants/accountDeletion';
import s from '@/features/UserChangeLogModal/ui/UserChangeLogModal.module.scss';

interface ClubChangeLogModalProps {
  clubId: number;
  clubName: string;
  onClose: () => void;
}

const LOG_PAGE_LIMIT = 20;

const clubFieldLabels: Record<string, string> = {
  name: 'Название клуба',
  region: 'Регион',
  lastName: 'Фамилия',
  firstName: 'Имя',
  patronymic: 'Отчество',
  legalName: 'Юр. лицо',
  contactPhone: 'Телефон',
  birthDate: 'Дата рождения',
  loginPrefix: 'Префикс',
  payerInn: 'ИНН',
  isActive: 'Активность',
  timeLimits: 'Игровое время',
  blockReason: 'Причина блокировки',
  accountStatus: 'Состояние аккаунта',
  balance_technocoins: 'Баланс технокоинов',
  balanceTechnocoins: 'Баланс технокоинов',
};

const renderLogAction = (log: ClubChangeLogItem): React.ReactNode => {
  if (log.field === 'timeLimits') {
    const raw = log.newValue ?? log.oldValue ?? '';
    let isSpend = false;
    try {
      const parsed = JSON.parse(String(raw)) as { action?: string };
      isSpend = parsed?.action === 'spendTime';
    } catch {
      isSpend = false;
    }
    return <span className={isSpend ? s.dangerValue : s.newValue}>{formatClubLogValue(log.field, raw)}</span>;
  }

  if (log.field === 'balance_technocoins' || log.field === 'balanceTechnocoins') {
    const label = log.type === 'deposit' ? 'Пополнение:' : log.type === 'withdrawal' ? 'Списание:' : 'Изменение:';

    const reasonNode = log.reason ? <span>{` (Причина: ${log.reason})`}</span> : null;

    return (
      <>
        <span>{label}</span>
        <span className={log.type === 'withdrawal' ? s.dangerValue : s.newValue}>
          {' '}
          {formatClubLogValue(log.field, log.newValue)}
        </span>
        {reasonNode}
      </>
    );
  }

  return (
    <>
      {log.type ? <span>{accountDeletionEventLabels[log.type] || log.type}</span> : null}
      <span>с</span>
      <span className={s.oldValue}>{formatClubLogValue(log.field, log.oldValue)}</span>
      <span>на</span>
      <span className={s.newValue}>{formatClubLogValue(log.field, log.newValue)}</span>
      {log.reason ? <span>Причина: {log.reason}</span> : null}
    </>
  );
};

export const ClubChangeLogModal: React.FC<ClubChangeLogModalProps> = ({ clubId, clubName, onClose }) => {
  const [offset, setOffset] = useState(0);
  const {
    data: logs,
    isLoading,
    isFetching,
    error,
  } = useGetClubChangeLogQuery({
    clubId,
    offset,
    limit: LOG_PAGE_LIMIT,
  });

  const handleLoadMore = () => {
    if (!isFetching) {
      setOffset((prev) => prev + LOG_PAGE_LIMIT);
    }
  };

  const hasMoreData = logs && logs.length > 0 && logs.length % LOG_PAGE_LIMIT === 0;

  return (
    <Modal open onClose={onClose} title={`История изменений клуба: ${clubName}`}>
      <div className={s.root}>
        <div className={s.logContainer}>
          {isLoading && offset === 0 && (
            <div className={s.loaderWrapper}>
              <Loader />
            </div>
          )}
          {error && <div>Ошибка загрузки истории изменений</div>}

          {!isLoading && offset === 0 && logs && logs.length === 0 && (
            <div className={s.emptyWrapper}>Изменений не найдено</div>
          )}

          {logs && logs.length > 0 && (
            <ul className={s.logList}>
              {logs.map((log) => (
                <li key={log.id} className={s.logItem}>
                  <div className={s.editorInfo}>
                    <span className={s.editorName}>{log.editor?.fullName || '—'}</span>
                    <span className={s.date}>{formatDateTimeForDisplay(log.createdAt)}</span>
                  </div>
                  <div className={s.action}>
                    <strong>{clubFieldLabels[log.field] || log.field}</strong>
                    {renderLogAction(log)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {hasMoreData && (
          <Button onClick={handleLoadMore} disabled={isFetching} className={s.loadMoreBtn}>
            {isFetching ? 'Загрузка...' : 'Показать ещё'}
          </Button>
        )}
      </div>
    </Modal>
  );
};
